'use client';

import * as _ from 'es-toolkit';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import clsx from 'clsx';
import { useState, useEffect } from 'react';
import { useSession, signOut, authClient } from '@/lib/auth-client';
import { useSignInModal } from '@/lib/ui';
import { useLockBodyScroll } from '@/hooks/use-lock-body-scroll';
import { useKeyboardShortcut } from '@/lib/contexts/KeyboardShortcutsContext';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { UserIcon, LogOutIcon, GridIcon, Menu, X, Shield, Search, MessageSquarePlus } from 'lucide-react';
import { useUserback } from '@userback/react';

/**
 * Safely access the Userback API. In embed mode or when no Userback token is
 * configured, the app renders without a UserbackProvider, in which case
 * `useUserback()` throws. This wrapper swallows that error so the Header can
 * render everywhere and simply omit the feedback action when unavailable.
 */
function useOptionalUserback() {
  try {
    return useUserback();
  } catch {
    return undefined;
  }
}
import Image from 'next/image';
import { SearchDialog } from '@/components/search/SearchDialog';
import { ImageLoader } from '@/components/ImageLoader';
import { MessagesButton } from '@/components/messaging/MessagesButton';
import { isApprovedTradePartner } from '@/lib/utils/trade';
import { useEarlyPreviewBanner } from '@/components/EarlyPreviewProvider';
interface HeaderProps {
  navItems?: {
    href: string;
    label: string;
  }[];
  searchEnabled?: boolean;
  signInEnabled?: boolean;
  /** When true, shows the "Early preview" label beneath the logo. Defaults to false. */
  showEarlyPreview?: boolean;
}
const DefaultNavItems = [{
  href: '/inspiration-feed',
  label: 'Inspiration'
}, {
  href: '/content/collaborations-central',
  label: 'Collaborate'
},
// { href: '/export', label: 'Export' },
{
  href: '/opportunities',
  label: 'Opportunities'
}, {
  href: '/directory',
  label: 'Directory'
}, {
  href: '/concierge',
  label: 'Free Help'
}
// { href: '/whatson', label: "What's On" },
// { href: '/hubs', label: 'Hubs' },
// { href: '/tools', label: 'Tools' },
// { href: '/spark', label: 'Spark' },
// { href: '/content?type=news', label: 'News' },
// { href: '/fun', label: 'Fun' },
// { href: '/asks', label: 'Asks' },
// { href: '/host-opportunities', label: 'Host' },
// process.env.SIZZLE_PLATFORM == 'sizzlenz' ? { href: '/nz-trade-hub', label: 'Trade Hub' } : null,
];
const MobileMenuSections = [{
  title: '',
  key: 'top',
  links: [{
    href: '/inspiration-feed',
    label: 'Inspiration'
  }, {
    href: '/content/collaborations-central',
    label: 'Collaborate'
  }, {
    href: '/demand',
    label: 'Master Demand 101'
  },
  // { href: '/export', label: 'Export Central' },
  {
    href: '/directory',
    label: 'Directory'
  }, {
    href: '/opportunities',
    label: 'Opportunities'
  }, {
    href: '/concierge',
    label: 'Free Help'
  }
  // { href: '/content?type=news', label: 'News' },
  // { href: '/whatson', label: "What's On" },
  // { href: '/spark', label: 'Spark' },
  // { href: '/fun', label: 'Fun' },
  // { href: '/consumer/discover-do', label: 'Discover & Do' },
  // { href: '/themes', label: 'Themes' },
  // { href: '/asks', label: 'Asks' },
  // { href: '/host-opportunities', label: 'Host' },
  // { href: '/hubs', label: 'Hubs' },
  // { href: '/tools', label: 'Tools' },
  // { href: '/collections', label: 'Collections' },
  ]
}];
export default function Header(props: HeaderProps) {
  const pathname = usePathname();
  const router = useRouter();
  const {
    data: session,
    isPending
  } = useSession();
  const {
    showSignInModal
  } = useSignInModal();
  const userback = useOptionalUserback();
  const navItems = props.navItems || DefaultNavItems;
  const showEarlyPreview = props.showEarlyPreview ?? false;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchDialogOpen, setIsSearchDialogOpen] = useState(false);
  const isBannerVisible = useEarlyPreviewBanner();

  // Check if currently impersonating
  const isImpersonating = !!session?.session?.impersonatedBy;

  // Register keyboard shortcut for search (Cmd+/ or Ctrl+/)
  useKeyboardShortcut('global-search', {
    key: '/',
    metaKey: typeof navigator !== 'undefined' && navigator.platform.includes('Mac'),
    ctrlKey: typeof navigator !== 'undefined' && !navigator.platform.includes('Mac')
  }, () => {
    if (props.searchEnabled) {
      setIsSearchDialogOpen(true);
    }
  }, props.searchEnabled ?? false);

  // Lock body scroll when mobile menu is open
  useLockBodyScroll(isMobileMenuOpen);
  const handleSignOut = async () => {
    await signOut();
    router.push('/');
    setIsMobileMenuOpen(false);
  };
  const handleStopImpersonating = async () => {
    try {
      await authClient.admin.stopImpersonating();
      router.push('/admin/users');
      window.location.reload();
    } catch (error) {
      console.error('Failed to stop impersonating:', error);
    }
  };
  const handleSignInClick = () => {
    showSignInModal({
      onSuccess: () => {
        // Optionally redirect or show success message
      }
    });
    setIsMobileMenuOpen(false);
  };
  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };
  const handleSendFeedback = () => {
    setIsMobileMenuOpen(false);
    try {
      userback?.open('general', 'form');
    } catch {
      // Userback not loaded — no-op
    }
  };

  // Handle escape key to close mobile menu
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isMobileMenuOpen) {
        closeMobileMenu();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isMobileMenuOpen]);
  return <>
      <header className={clsx('fixed left-0 right-0 z-40 bg-white/80 backdrop-blur-md border-b border-gray-200 shadow-sm transition-[top] duration-200', isBannerVisible ? 'top-[36px]' : 'top-0')}>
        {/* Impersonation Banner */}
        {isImpersonating && <div className="bg-amber-500 text-white text-center py-1.5 text-sm font-medium">
            ⚠️ You are impersonating {session?.user?.name || session?.user?.email}
            <button onClick={handleStopImpersonating} className="ml-3 underline hover:no-underline cursor-pointer">
              Stop Impersonating
            </button>
          </div>}
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
          <div className="relative flex justify-between items-center h-24 min-w-0">
            {/* Free Help - centered on mobile (desktop shows it in the nav) */}
            <Link href="/concierge" className={clsx('md:hidden absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 px-2 py-1.5 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap', pathname === '/concierge' ? 'bg-primary-50 text-primary-600' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50')} onClick={closeMobileMenu}>
              Free Analysis
            </Link>
            <Link href="/" className="flex flex-col items-start gap-1 flex-shrink-0 cursor-pointer" onClick={closeMobileMenu}>
              <Image src="/sizzle-logo-svg.svg" alt="Sizzle" width={1955} height={763} priority className="h-14 w-auto" />
              {showEarlyPreview && <span className="text-[9px] font-semibold uppercase tracking-wider text-primary-600 leading-none">
                  Early preview
                </span>}
            </Link>

            <nav className="hidden md:flex items-center gap-3">
              {navItems.filter(item => {
              if (!item) return false;
              // Filter out trade partner items if user is not a trade partner
              if ('requiresTradePartner' in item && item.requiresTradePartner) {
                return session?.user && isApprovedTradePartner((session.user as any)?.featureFlags);
              }
              return true;
            }).map(item_0 => <Link key={item_0.href} href={item_0.href} className={clsx('px-2 py-1.5 rounded-lg text-sm font-medium transition-all cursor-pointer', pathname === item_0.href ? 'bg-primary-50 text-primary-600' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50')}>
                    {item_0.label}
                  </Link>)}
            </nav>

            <div className="flex items-center gap-2 sm:gap-2 flex-shrink-0">
              {/* Search Button */}
              {props.searchEnabled && <button onClick={() => setIsSearchDialogOpen(true)} className="p-1.5 sm:p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-all cursor-pointer flex-shrink-0" aria-label="Search">
                  <Search className="w-5 h-5" />
                </button>}

              {/* Messages Button - Only show for authenticated users */}
              {/* {session && (
                <Link href="/messaging">
                  <MessagesButton onClick={() => {}} />
                </Link>
               )} */}

              {/* Auth Section - Hidden on mobile when menu is open */}
              <div className={clsx('flex items-center flex-shrink-0', isMobileMenuOpen && 'hidden md:flex')}>
                {isPending ? <div className="w-8 h-8 rounded-full bg-gray-200 animate-pulse" /> : session ? <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3">
                        <div className="relative">
                          <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                            {session.user.image ? <ImageLoader src={session.user.image} alt={session.user.name || 'User'} width={32} height={32} className="w-full h-full rounded-full" /> : <UserIcon className="w-4 h-4 text-primary-600" />}
                          </div>
                          {!(session.user as any)?.approved && <span className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-amber-500 border-2 border-white rounded-full" />}
                        </div>
                        {/* <span className="hidden sm:inline text-sm font-medium truncate max-w-[120px]">
                          {session.user.name || session.user.email}
                         </span> */}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      {isImpersonating && <>
                          <div className="px-2 py-1.5 text-xs font-semibold text-amber-600 bg-amber-50 border-b border-amber-100">
                            ⚠️ Impersonating User
                          </div>
                        </>}
                      <DropdownMenuItem disabled className="flex items-center justify-between">
                        <div className="flex flex-col">
                          <div className="font-medium">
                            {session.user.name || session.user.email}
                          </div>
                          {session.user.name && <div className="text-xs text-gray-500">{session.user.email}</div>}
                          <div className="text-xs text-gray-600 mt-1">
                            {(session.user as any)?.pointsBalance ?? 0} points
                          </div>
                          {!(session.user as any)?.approved && <span className="text-[10px] bg-amber-50 text-amber-600 px-1.5 py-0.5 rounded-full font-bold border border-amber-100 uppercase tracking-wider mt-1">
                              Pending Approval
                            </span>}
                        </div>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link href="/me" className="flex items-center justify-between cursor-pointer w-full">
                          <span className="flex items-center">
                            <UserIcon className="mr-2 h-4 w-4" />
                            My Dashboard
                          </span>
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link href="/me/collections" className="flex items-center justify-between cursor-pointer w-full">
                          <span className="flex items-center">
                            <GridIcon className="mr-2 h-4 w-4" />
                            My Collections
                          </span>
                        </Link>
                      </DropdownMenuItem>
                      {(session.user as any)?.isAdmin && <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem asChild>
                            <Link href="/admin" className="flex items-center justify-between cursor-pointer w-full">
                              <span className="flex items-center">
                                <Shield className="mr-2 h-4 w-4" />
                                Admin Dashboard
                              </span>
                            </Link>
                          </DropdownMenuItem>
                        </>}
                      {isImpersonating && <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={handleStopImpersonating} className="cursor-pointer text-amber-600 font-medium">
                            <UserIcon className="mr-2 h-4 w-4" />
                            Stop Impersonating
                          </DropdownMenuItem>
                        </>}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer">
                        <LogOutIcon className="mr-2 h-4 w-4" />
                        Sign Out
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu> : <Button size="sm" className="shadow-sm hover:shadow px-3 sm:px-4" onClick={handleSignInClick}>
                    Sign In
                  </Button>}
              </div>
              {/* Menu Button */}
              <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="p-1.5 sm:p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-all flex-shrink-0 cursor-pointer" aria-label="Toggle menu">
                {isMobileMenuOpen ? <X className="w-5 h-5 sm:w-6 sm:h-6" /> : <Menu className="w-5 h-5 sm:w-6 sm:h-6" />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Menu Overlay */}
      {isMobileMenuOpen && <div className="fixed inset-0 z-50 bg-white flex flex-col animate-in slide-in-from-top-2 duration-300">
          {/* Header in mobile menu */}
          <div className="flex-shrink-0 border-b border-gray-100 px-4 py-4">
            <div className="flex justify-between items-center">
              <Link href="/" className="flex flex-col items-start gap-1 cursor-pointer" onClick={closeMobileMenu}>
                <Image src="/sizzle-logo-svg.svg" alt="Sizzle" width={1955} height={763} className="h-14 w-auto" />
                {showEarlyPreview && <span className="text-[9px] font-semibold uppercase tracking-wider text-primary-600 leading-none">
                    Early preview
                  </span>}
              </Link>
              <button onClick={closeMobileMenu} className="p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-all" aria-label="Close menu">
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Mobile Menu Content - Scrollable */}
          <div className="flex-1 overflow-y-auto px-4 py-6">
            {/* Navigation Sections */}
            <div className="space-y-8">
              {MobileMenuSections.map(section => <div key={section.key}>
                  {section.title && <h3 className="text-sm font-bold text-gray-900 uppercase tracking-widest mb-4 text-right">
                      {section.title}
                    </h3>}
                  <div className="space-y">
                    {section.links.filter(link => {
                // Filter out trade partner links if user is not a trade partner
                if ('requiresTradePartner' in link && link.requiresTradePartner) {
                  return session?.user && isApprovedTradePartner((session.user as any)?.featureFlags);
                }
                return true;
              }).map(link_0 => <Link key={link_0.href} href={link_0.href} className={clsx('block px-3 py-1 rounded-lg text-lg font-medium transition-all text-right', pathname === link_0.href ? 'bg-primary-50 text-primary-600' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50')} onClick={closeMobileMenu}>
                          {link_0.label}
                        </Link>)}
                  </div>
                </div>)}

              {/* Account section - only for signed-in users */}
              {session && <div>
                  <div className="space-y">
                    <Link href="/me/following" className={clsx('block px-3 py-1 rounded-lg text-lg font-medium transition-all text-right', pathname === '/me/following' ? 'bg-primary-50 text-primary-600' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50')} onClick={closeMobileMenu}>
                      Following / Saved
                    </Link>
                  </div>
                </div>}
            </div>

            {/* Footer Links */}
            <div className="mt-12 pt-8 border-t border-gray-100">
              <div className="flex flex-col gap-4 items-end">
                {userback && <button onClick={handleSendFeedback} className="flex items-center gap-2 text-lg font-medium text-gray-700 hover:text-primary-600 transition-colors cursor-pointer">
                    <MessageSquarePlus className="w-5 h-5" />
                    Send Feedback
                  </button>}
                <div className="flex items-center gap-6">
                  <a href="mailto:hello@sizzle.nz" className="flex items-center gap-2 text-gray-500 hover:text-primary-600 transition-colors">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M1.5 8.67v8.58a3 3 0 003 3h15a3 3 0 003-3V8.67l-8.928 5.493a1.75 1.75 0 01-1.644 0L1.5 8.67z" />
                      <path d="M22.5 6.908V6.75a3 3 0 00-3-3h-15a3 3 0 00-3 3v.158l9.714 5.978a.25.25 0 00.235 0L22.5 6.908z" />
                    </svg>
                    Contact
                  </a>
                  <a href="https://linkedin.com/company/sizzlenz" target="_blank" rel="noopener" className="flex items-center gap-2 text-gray-500 hover:text-primary-600 transition-colors">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.5 2h-17A1.5 1.5 0 002 3.5v17A1.5 1.5 0 003.5 22h17a1.5 1.5 0 001.5-1.5v-17A1.5 1.5 0 0020.5 2zM8 19H5v-9h3zM6.5 8.25A1.75 1.75 0 118.25 6.5 1.75 1.75 0 016.5 8.25zM19 19h-3v-4.74c0-1.42-.6-1.93-1.38-1.93A1.74 1.74 0 0013 14.19V19h-3v-9h2.9v1.3a3.11 3.11 0 012.7-1.54c1.39 0 3.4.81 3.4 4V19z" />
                    </svg>
                    LinkedIn
                  </a>
                </div>
                <p className="text-sm text-gray-500">
                  © {new Date().getFullYear()} Sizzle CPG. All rights reserved.
                </p>
              </div>
            </div>
          </div>
        </div>}

      {/* Search Dialog */}
      {props.searchEnabled && <SearchDialog open={isSearchDialogOpen} onOpenChange={setIsSearchDialogOpen} />}
    </>;
}